import { LessonDefinition } from "../../types";

const lesson: LessonDefinition = {
  id: "search-and-ask",
  courseId: "policies",
  order: 1,
  title: "Search and ask",
  subtitle: "Browse documents, ask questions",
  summary:
    "Search the policy manual, directives, and municipal code your admin uploaded, open any section on its exact page, or ask a chat that answers from those documents and cites the section and page.",
  learn: [
    "Search the document list by title or topic, and find a section inside a document",
    "Download the original document",
    "Start a chat, use the suggested questions, and follow a citation to the section",
    "Know the limits of what the chat answers",
  ],
  minutes: 4,
  releasedAt: "2026-10-08T00:00:00.000Z",
  gate: "agency",
  sceneId: "search-and-ask",
  tourId: "search-and-ask",
  body: [
    {
      type: "paragraph",
      text: "**Policies** appears in the sidebar once your department has at least one processed document. Each document is split into numbered sections (300.3.1 style for Lexipol manuals, § and Sec. for code) and tagged by topic, so you can find things by subject instead of scrolling. Reading the manual is slow at 0200, so the page has two tabs: **Documents** to browse and search, and **Chat** to ask. Either way, the manual stays the authority.",
    },
    { type: "heading", text: "Documents tab: search and open" },
    {
      type: "steps",
      items: [
        "Click **Policies**. The **Documents** tab lists every document.",
        "Click the search box, \"Search policies and topics\", and type a document title or a topic tag such as \"force\". The list narrows to the documents that match.",
      ],
    },
    {
      type: "paragraph",
      text: "The list search matches document titles and topic tags. To find a specific section, open the document and use its outline (next).",
    },
    {
      type: "steps",
      items: [
        "Open a document. The page viewer is on one side and the sections outline is on the other.",
        "Type in \"Find a section or topic\" to jump to a section.",
        "Hover a section in the outline and its box lights up on the page.",
        "Click the section and the page jumps to it, with the section selected.",
        "Click **Download** to get the original file.",
      ],
    },
    {
      type: "callout",
      tone: "note",
      title: "Older documents",
      text: "Documents uploaded before section highlighting was added show no boxes until the admin reprocesses them. The text search still works.",
    },
    {
      type: "paragraph",
      text: "Links from the editor, such as a policy check suggestion or a Dept-badged statute, land on this same viewer with the section already selected.",
    },
    { type: "heading", text: "Chat tab: ask a question" },
    {
      type: "paragraph",
      text: "The Chat tab answers from your department documents and shows exactly where each answer came from.",
    },
    {
      type: "steps",
      items: [
        "Open **Policies** and click the **Chat** tab. Or click **Ask** on the Documents tab or on a document to open the chat in a side drawer.",
        "With no earlier chats, the page opens ready for a new one. Click **New chat** in the Chats list later to start another.",
        "Pick one of the suggested questions, or type your own in \"Ask anything about {dept}'s policies…\".",
        "Read the answer, and click a numbered citation, or a chip under Sources, to open the cited section on its page in the document viewer.",
      ],
    },
    {
      type: "paragraph",
      text: "The suggested questions are built from your department's actual documents. Threads are saved, so you can return to one from the Chat tab.",
    },
    {
      type: "callout",
      tone: "tip",
      text: "Ask the way the question would come up on a call. \"Can I tow an unregistered vehicle from private property?\" gets a better answer than \"towing\".",
    },
    {
      type: "callout",
      tone: "note",
      title: "Limits",
      text: "The assistant answers only from the uploaded documents. It does not know state law or other departments' policies. If the manual does not cover your question, it will say so.",
    },
  ],
  related: ["policy-check-suggestions", "department-documents"],
};

export default lesson;
